import React, { useState, useEffect } from 'react';
import axios from './utils/axiosConfig';
import { Container, Row, Col, Card, Spinner, Alert, Form, Button, Table, Badge, Modal, Tabs, Tab } from 'react-bootstrap';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title } from 'chart.js';
import { Pie, Bar } from 'react-chartjs-2';
import 'bootstrap/dist/css/bootstrap.min.css';
import { useAuth } from './context/AuthContext';
import { downloadExcel } from './utils/excel';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title);

const MemberParticipationAnalysis = () => {
  const [analysisData, setAnalysisData] = useState(null);
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [storesLoading, setStoresLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberDetail, setMemberDetail] = useState(null);
  const [activeTab, setActiveTab] = useState('participated');
  const [chartKey, setChartKey] = useState(0); // 用於強制重新渲染圖表
  const token = localStorage.getItem('token');

  // 初始化日期為最近三個月
  useEffect(() => {
    const today = new Date();
    const threeMonthsAgo = new Date();
    threeMonthsAgo.setMonth(today.getMonth() - 3);
    
    setEndDate(today.toISOString().split('T')[0]);
    setStartDate(threeMonthsAgo.toISOString().split('T')[0]);
  }, []);

  const fetchStores = async () => {
    setStoresLoading(true);
    
    if (!token) {
      setError('請先登入系統才能查看分析資料');
      setStores([]);
      setStoresLoading(false);
      return;
    }
    
    try {
      const response = await axios.get('/member-participation/stores', {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      
      if (response.data && response.data.success && response.data.data) {
        setStores(response.data.data);
      } else {
        setStores([]);
      }
    } catch (error) {
      console.error('Error fetching stores:', error);
      if (error.response?.status === 401) {
        setError('登入已過期，請重新登入');
      } else {
        setError('無法載入社別資料，請稍後再試');
      }
      setStores([]);
    }
    setStoresLoading(false);
  };

  const fetchAnalysisData = async () => {
    setLoading(true);
    
    if (!token) {
      setError('請先登入系統才能查看分析資料');
      setLoading(false);
      return;
    }
    
    try {
      const params = {
        startDate: startDate,
        endDate: endDate
      };
      
      if (selectedStore) {
        params.ss_id = selectedStore;
      }
      
      const response = await axios.get('/member-participation/member-participation-analysis', {
        headers: { 'Authorization': `Bearer ${token}` },
        params
      });
      
      setAnalysisData(response.data.data);
      setError(null);
      setChartKey(prev => prev + 1); // 強制重新渲染圖表
    } catch (error) {
      console.error('Error fetching analysis data:', error);
      if (error.response?.status === 401) {
        setError('登入已過期，請重新登入');
      } else {
        setError('無法載入分析資料，請稍後再試。');
      }
    }
    setLoading(false);
  };

  const fetchMemberDetail = async (memberId) => {
    try {
      const response = await axios.get(`/member-participation/member-participation-detail/${memberId}`, {
        headers: { 'Authorization': `Bearer ${token}` },
        params: {
          startDate: startDate,
          endDate: endDate
        }
      });
      
      setMemberDetail(response.data.data);
    } catch (error) {
      console.error('Error fetching member detail:', error);
      alert('無法載入社友詳細資料');
    }
  };

  useEffect(() => {
    fetchStores();
    
    // 清理函數
    return () => {
      setAnalysisData(null);
      setSelectedMember(null);
      setMemberDetail(null);
      setShowDetailModal(false);
    };
  }, []);

  useEffect(() => {
    if (startDate && endDate) {
      // 清理之前的數據，避免狀態衝突
      setAnalysisData(null);
      setSelectedMember(null);
      setMemberDetail(null);
      
      fetchAnalysisData();
    }
  }, [startDate, endDate, selectedStore]);

  const handleSearch = () => {
    fetchAnalysisData();
  };

  const handleMemberClick = async (member) => {
    setSelectedMember(member);
    setShowDetailModal(true);
    await fetchMemberDetail(member.e_id);
  };

  const handleDownloadExcel = async () => {
    if (!analysisData || !analysisData.participatedMembers || analysisData.participatedMembers.length === 0) {
      alert('沒有資料可以下載');
      return;
    }

    try {
      // 準備要匯出的資料，移除操作詳細欄位
      const exportData = analysisData.participatedMembers.map(member => ({
        姓名: member.e_name,
        社別: member.ss_name,
        組別: member.e_set,
        職位: member.position_name || member.e_set_position,
        參與活動數: member.activity_count,
        參與人次: member.participation_count,
        月例會次數: member.monthly_meeting_count || 0,
        理監事會次數: member.board_meeting_count || 0,
        最後參與: member.last_participation ? new Date(member.last_participation).toLocaleDateString('zh-TW') : ''
      }));

      const fileName = `${getCurrentStoreName()}社友參與狀況分析_${startDate}_${endDate}.xlsx`;
      
      // 使用通用的下載函數
      await downloadExcel(exportData, 'participation_analysis', fileName);
      
    } catch (error) {
      console.error('下載 Excel 失敗:', error);
      alert('下載失敗，請稍後再試');
    }
  };

  const getCurrentStoreName = () => {
    if (!selectedStore) return '全國';
    const store = stores.find(s => s.ss_id.toString() === selectedStore);
    return store ? store.ss_name : '全國';
  };

  const generateParticipationRateChart = () => {
    if (!analysisData || !analysisData.summary) return null;

    const { summary } = analysisData;
    const participatedRate = summary.participationRate || 0;
    const nonParticipatedRate = 100 - participatedRate;

    // 確保數據有效
    if (isNaN(participatedRate) || participatedRate < 0) return null;

    return {
      labels: [`有參與 (${participatedRate}%)`, `未參與 (${nonParticipatedRate.toFixed(1)}%)`],
      datasets: [{
        label: '參與率',
        data: [participatedRate, nonParticipatedRate],
        backgroundColor: ['#36A2EB', '#FF6384'],
        borderColor: ['#36A2EB', '#FF6384'],
        borderWidth: 1
      }]
    };
  };

  const generateParticipationGroupsChart = () => {
    if (!analysisData || !analysisData.participationGroups) return null;

    const { participationGroups } = analysisData;
    const { high = 0, medium = 0, low = 0, none = 0 } = participationGroups;

    // 確保所有數據都是有效數字
    const data = [high, medium, low, none];
    if (data.some(val => isNaN(val) || val < 0)) return null;

    return {
      labels: ['高度參與 (5次以上)', '中度參與 (2-4次)', '低度參與 (1次)', '未參與'],
      datasets: [{
        label: '人數',
        data: data,
        backgroundColor: ['#4BC0C0', '#36A2EB', '#FFCE56', '#FF6384'],
        borderColor: ['#4BC0C0', '#36A2EB', '#FFCE56', '#FF6384'],
        borderWidth: 1
      }]
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          boxWidth: 12,
          padding: 8,
          font: {
            size: 11
          }
        }
      }
    }
  };

  const barChartOptions = {
    ...chartOptions,
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1
        }
      }
    }
  };

  if (loading) {
    return (
      <Container className="mt-4 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">載入中...</span>
        </Spinner>
        <p className="mt-2">載入社友參與分析資料中...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">
          <Alert.Heading>無法載入資料</Alert.Heading>
          <p>{error}</p>
          {error.includes('登入') && (
            <>
              <hr />
              <div className="d-flex justify-content-end">
                <Button variant="outline-danger" href="/login">
                  前往登入頁面
                </Button>
              </div>
            </>
          )}
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="mt-4">
      <Row className="mb-4">
        <Col>
          <h2 className="text-center">{getCurrentStoreName()}社友參與狀況分析</h2>
          <p className="text-center text-muted">分析社友在指定期間內的活動參與情況，包含正查詢和反查詢功能</p>
        </Col>
      </Row>
      
      {/* 查詢條件 */}
      <Row className="mb-4">
        <Col>
          <Card>
            <Card.Body>
              <Row>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label><strong>開始日期：</strong></Form.Label>
                    <Form.Control 
                      type="date" 
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label><strong>結束日期：</strong></Form.Label>
                    <Form.Control 
                      type="date" 
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label><strong>選擇社別：</strong></Form.Label>
                    <Form.Select 
                      value={selectedStore} 
                      onChange={(e) => setSelectedStore(e.target.value)}
                      disabled={storesLoading}
                    >
                      <option value="">全部社友</option>
                      {!storesLoading && stores && stores.length > 0 && stores.map(store => (
                        <option key={store.ss_id} value={store.ss_id}>
                          {store.ss_name}
                        </option>
                      ))}
                      {storesLoading && (
                        <option disabled>載入社別資料中...</option>
                      )}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={2} className="d-flex align-items-end">
                  <div className="d-grid gap-2 w-100">
                    <Button variant="primary" onClick={handleSearch}>
                      查詢
                    </Button>
                    <Button 
                      variant="success" 
                      onClick={handleDownloadExcel}
                      disabled={!analysisData || !analysisData.participatedMembers || analysisData.participatedMembers.length === 0}
                    >
                      下載Excel
                    </Button>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {analysisData && (
        <>
          {/* 統計摘要 */}
          <Row className="mb-4">
            <Col>
              <Card>
                <Card.Header>
                  <h5>統計摘要</h5>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={2} className="text-center mb-3">
                      <h4 className="text-info">{analysisData.summary?.totalMembers || 0}</h4>
                      <p>總社友數</p>
                    </Col>
                    <Col md={2} className="text-center mb-3">
                      <h4 className="text-primary">{analysisData.summary?.totalActivities || 0}</h4>
                      <p>期間活動數</p>
                    </Col>
                    <Col md={2} className="text-center mb-3">
                      <h4 className="text-success">{analysisData.summary?.participatedCount || 0}</h4>
                      <p>有參與人數</p>
                    </Col>
                    <Col md={2} className="text-center mb-3">
                      <h4 className="text-danger">{analysisData.summary?.nonParticipatedCount || 0}</h4>
                      <p>未參與人數</p>
                    </Col>
                    <Col md={2} className="text-center mb-3">
                      <h4 className="text-warning">{analysisData.summary?.participationRate || 0}%</h4>
                      <p>參與率</p>
                    </Col>
                    <Col md={2} className="text-center mb-3">
                      <h4 className="text-secondary">{analysisData.summary?.averageParticipationPerMember || 0}</h4>
                      <p>平均參與次數</p>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* 圖表顯示 */}
          <Row className="mb-4">
            <Col lg={6} className="mb-4">
              <Card>
                <Card.Header>
                  <h5>參與率分析</h5>
                </Card.Header>
                <Card.Body style={{ height: '400px' }}>
                  {generateParticipationRateChart() ? (
                    <Pie 
                      key={`pie-${chartKey}`}
                      data={generateParticipationRateChart()} 
                      options={chartOptions}
                    />
                  ) : (
                    <div className="d-flex align-items-center justify-content-center h-100">
                      <p className="text-muted">暫無資料可顯示</p>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>

            <Col lg={6} className="mb-4">
              <Card>
                <Card.Header>
                  <h5>參與程度分析</h5>
                </Card.Header>
                <Card.Body style={{ height: '400px' }}>
                  {generateParticipationGroupsChart() ? (
                    <Bar 
                      key={`bar-${chartKey}`}
                      data={generateParticipationGroupsChart()} 
                      options={barChartOptions}
                    />
                  ) : (
                    <div className="d-flex align-items-center justify-content-center h-100">
                      <p className="text-muted">暫無資料可顯示</p>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* 詳細社友列表 */}
          <Row className="mb-4">
            <Col>
              <Card>
                <Card.Header>
                  <h5>社友參與詳細列表</h5>
                </Card.Header>
                <Card.Body>
                  <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-3">
                    <Tab eventKey="participated" title={`有參與社友 (${analysisData.participatedMembers?.length || 0}人)`}>
                      <Table striped bordered hover responsive>
                        <thead>
                          <tr className="table-success">
                            <th>姓名</th>
                            <th>社別</th>
                            <th>組別</th>
                            <th>職位</th>
                            <th>參與活動數</th>
                            <th>參與人次</th>
                            <th>月例會次數</th>
                            <th>理監事會次數</th>
                            <th>最後參與</th>
                            <th>操作</th>
                          </tr>
                        </thead>
                        <tbody>
                          {analysisData.participatedMembers && analysisData.participatedMembers.map((member, index) => (
                            <tr key={`participated-${member.e_id}-${index}`}>
                              <td><strong>{member.e_name}</strong></td>
                              <td>{member.ss_name}</td>
                              <td>{member.e_set}</td>
                              <td>{member.position_name || member.e_set_position}</td>
                              <td>
                                <Badge bg="primary">{member.activity_count}</Badge>
                              </td>
                              <td>
                                <Badge bg="success">{member.participation_count}</Badge>
                              </td>
                              <td>
                                <Badge bg="info">{member.monthly_meeting_count || 0}</Badge>
                              </td>
                              <td>
                                <Badge bg="warning">{member.board_meeting_count || 0}</Badge>
                              </td>
                              <td>{member.last_participation ? new Date(member.last_participation).toLocaleDateString('zh-TW') : '-'}</td>
                              <td>
                                <Button 
                                  variant="outline-info" 
                                  size="sm"
                                  onClick={() => handleMemberClick(member)}
                                >
                                  詳細
                                </Button>
                              </td>
                            </tr>
                          ))}
                          {(!analysisData.participatedMembers || analysisData.participatedMembers.length === 0) && (
                            <tr>
                              <td colSpan="10" className="text-center text-muted">
                                查詢期間內沒有社友參與活動
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </Tab>

                    <Tab eventKey="non-participated" title={`未參與社友 (${analysisData.nonParticipatedMembers?.length || 0}人)`}>
                      <Table striped bordered hover responsive>
                        <thead>
                          <tr className="table-warning">
                            <th>姓名</th>
                            <th>社別</th>
                            <th>組別</th>
                            <th>職位</th>
                            <th>狀態</th>
                          </tr>
                        </thead>
                        <tbody>
                          {analysisData.nonParticipatedMembers && analysisData.nonParticipatedMembers.map((member, index) => (
                            <tr key={`non-participated-${member.e_id}-${index}`}>
                              <td><strong>{member.e_name}</strong></td>
                              <td>{member.ss_name}</td>
                              <td>{member.e_set}</td>
                              <td>{member.position_name || member.e_set_position}</td>
                              <td>
                                <Badge bg="danger">未參與</Badge>
                              </td>
                            </tr>
                          ))}
                          {(!analysisData.nonParticipatedMembers || analysisData.nonParticipatedMembers.length === 0) && (
                            <tr>
                              <td colSpan="5" className="text-center text-muted">
                                所有社友都有參與活動
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </Tab>
                  </Tabs>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      )}

      {/* 社友詳細資料模態框 */}
      <Modal 
        show={showDetailModal} 
        onHide={() => setShowDetailModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>社友參與詳細記錄</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedMember && (
            <div className="mb-3">
              <h5>{selectedMember.e_name} - {selectedMember.ss_name}</h5>
              <p>
                <strong>組別：</strong>{selectedMember.e_set} | 
                <strong> 職位：</strong>{selectedMember.position_name || selectedMember.e_set_position}
              </p>
            </div>
          )}
          
          {memberDetail ? (
            <>
              <div className="mb-3">
                <Badge bg="primary">總參與次數: {memberDetail.participationCount}</Badge>
              </div>
              
              <Table striped bordered hover size="sm">
                <thead>
                  <tr>
                    <th>活動名稱</th>
                    <th>活動日期</th>
                    <th>活動類別</th>
                    <th>參與狀態</th>
                    <th>報名時間</th>
                    <th>簽到時間</th>
                    <th>簽到方式</th>
                  </tr>
                </thead>
                <tbody>
                  {memberDetail.participationDetail.map((record, index) => (
                    <tr key={index}>
                      <td>{record.a_name}</td>
                      <td>{record.a_date}</td>
                      <td>{record.activity_category}</td>
                      <td>
                        <Badge bg={
                          record.participation_type === 'checked_in' ? 'success' :
                          record.participation_type === 'registered_only' ? 'primary' :
                          record.participation_type === 'leave' ? 'warning' : 'secondary'
                        }>
                          {record.participation_type === 'checked_in' ? '已簽到' :
                           record.participation_type === 'registered_only' ? '已報名' :
                           record.participation_type === 'leave' ? '請假' : '未知'}
                        </Badge>
                      </td>
                      <td>{record.registered_time ? new Date(record.registered_time).toLocaleString('zh-TW') : '-'}</td>
                      <td>{record.checkin_time ? new Date(record.checkin_time).toLocaleString('zh-TW') : '-'}</td>
                      <td>
                        {record.checkin_method ? (
                          <Badge bg="secondary">
                            {record.checkin_method === 'mobile' ? '手機' : 
                             record.checkin_method === 'line' ? 'LINE' : 
                             record.checkin_method === 'face' ? '人臉' : record.checkin_method}
                          </Badge>
                        ) : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </>
          ) : (
            <div className="text-center">
              <Spinner animation="border" size="sm" />
              <p>載入詳細資料中...</p>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailModal(false)}>
            關閉
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default MemberParticipationAnalysis;